import { useTheme } from "next-themes"
import { useMsal } from "@azure/msal-react"
import Image from "next/image"
import { Sun, Moon } from "lucide-react"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu"
import { TSiteConfiguration } from "@/types/base"
import { getInitials } from "@/lib/utils"
import { ArrowUpRight, Menu } from "lucide-react"
interface ThemeToggleProps {
  data: TSiteConfiguration
}

/**
 * ThemeToggle component handles theme switching and user profile display.
 * Implements a clean separation of concerns with dedicated hooks for business logic.
 */
const Header = ({ data }: ThemeToggleProps) => {
  const { currentTheme, toggleTheme } = useThemeToggle()
  const { userInitials } = useUserProfile()

  return (
    <header className="flex items-center justify-between pb-4 lg:pb-0">
      <SiteLogo data={data} />
      <div className="flex items-center gap-3">
        {/* Desktop navigation - hidden on screens < 900px */}
        <div className="hidden min-[900px]:flex items-center gap-6 mr-4">
          <a
            href="https://unitedtalent.box.com/s/09eosqqzct7gqz80qxd1t4u9ukzodapk"
            target="_blank"
            className="text-base flex items-center gap-1"
          >
            Company Deck
            <ArrowUpRight className="w-4 h-4 text-gray-400 dark:text-gray-600" />
          </a>
          <a
            href="https://unitedtalent.box.com/s/uyf9lmzihh6qrts1bucg6p1gdcgeytiq"
            target="_blank"
            className="text-base flex items-center gap-1"
          >
            Brand Anthem Video
            <ArrowUpRight className="w-4 h-4 text-gray-400 dark:text-gray-600" />
          </a>
          <a
            href="https://myapps.microsoft.com/"
            target="_blank"
            className="text-base flex items-center gap-1"
          >
            Work Apps
            <ArrowUpRight className="w-4 h-4 text-gray-400 dark:text-gray-600" />
          </a>
          <a
            href="https://unitedtalent.box.com/v/UTA-RESOURCES"
            target="_blank"
            className="text-base flex items-center gap-1"
          >
            Employee Resources
            <ArrowUpRight className="w-4 h-4 text-gray-400 dark:text-gray-600" />
          </a>
        </div>

        {/* Mobile navigation dropdown - shown on screens < 900px */}
        <NavigationDropdown />

        <ThemeDropdown
          currentTheme={currentTheme}
          onThemeToggle={toggleTheme}
          userInitials={userInitials}
        />
      </div>
    </header>
  )
}

export default Header

/**
 * Custom hook encapsulating theme-related logic
 */
const useThemeToggle = () => {
  const { theme, setTheme } = useTheme()

  const toggleTheme = () => {
    if (theme === "system") {
      const systemIsDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches
      setTheme(systemIsDark ? "light" : "dark")
    } else {
      setTheme(theme === "dark" ? "light" : "dark")
    }
  }

  const currentTheme =
    theme === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : theme

  return {
    currentTheme,
    toggleTheme,
  }
}

/**
 * Custom hook encapsulating user profile logic
 */
const useUserProfile = () => {
  const { accounts } = useMsal()
  const user = accounts[0]

  return {
    userInitials: getInitials(user?.name || ""),
  }
}

interface SiteLogoProps {
  data: TSiteConfiguration
}

/**
 * Reusable SiteLogo component with proper image attributes
 */
const SiteLogo = ({ data }: SiteLogoProps) => (
  <Image
    width={50}
    height={52}
    src="/logo.svg"
    alt={data?.logo?.title || "uta-logo"}
    className="dark:invert"
    priority
  />
)

interface ThemeDropdownProps {
  currentTheme?: string
  onThemeToggle: () => void
  userInitials: string
}

/**
 * ThemeDropdown component with proper accessibility attributes
 * and clean styling organization
 */
const ThemeDropdown = ({
  currentTheme,
  onThemeToggle,
  userInitials,
}: ThemeDropdownProps) => {
  const ThemeIcon = currentTheme === "dark" ? Sun : Moon
  const themeLabel = `Switch to ${currentTheme === "dark" ? "light" : "dark"} theme`

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Avatar>
          <AvatarFallback className="flex items-center justify-center">
            {userInitials}
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[186px] border-border shadow-md"
      >
        <DropdownMenuItem
          onClick={onThemeToggle}
          aria-label={themeLabel}
          className="flex items-center gap-2 text-sm font-normal leading-relaxed 
                   cursor-pointer hover:rounded-none focus:rounded-none dark:hover:bg-secondary"
        >
          <ThemeIcon className="h-4 w-4" />
          <span>Toggle Theme</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * NavigationDropdown component for mobile navigation
 * Shows navigation links in a dropdown menu on screens < 900px
 */
const NavigationDropdown = () => {
  const navigationItems = [
    {
      href: "https://unitedtalent.box.com/s/09eosqqzct7gqz80qxd1t4u9ukzodapk",
      label: "Company Deck",
    },
    {
      href: "https://unitedtalent.box.com/s/uyf9lmzihh6qrts1bucg6p1gdcgeytiq",
      label: "Brand Anthem Video",
    },
    {
      href: "https://myapps.microsoft.com/",
      label: "Work Apps",
    },
    {
      href: "https://unitedtalent.box.com/v/UTA-RESOURCES",
      label: "Employee Resources",
    },
  ]

  return (
    <div className="min-[900px]:hidden">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex items-center justify-center w-10 h-10  dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 mr-2"
            aria-label="Open navigation menu"
          >
            <Menu className="w-8 h-8" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-[200px] border-border shadow-md"
        >
          {navigationItems.map((item) => (
            <DropdownMenuItem key={item.href} asChild>
              <a
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm font-normal leading-relaxed cursor-pointer hover:rounded-none focus:rounded-none dark:hover:bg-secondary"
              >
                <span>{item.label}</span>
                <ArrowUpRight className="w-3 h-3 text-gray-400 dark:text-gray-600" />
              </a>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
